import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Calendar as CalendarIcon, Loader2, Package, Clock, Trash2 } from "lucide-react";
import { format, addDays, differenceInHours } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface EditPickupModalProps {
  shipmentId: string;
  currentBoxIds: string[];
  currentPickupDate: Date;
  currentTimeSlot: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Box {
  id: string;
  box_number: string;
  name: string;
  room: string;
  location_status: string;
  hasScheduledPickup?: boolean;
}

const timeSlotDisplay: Record<string, string> = {
  '9-11am': '9:00 AM - 11:00 AM',
  '12-2pm': '12:00 PM - 2:00 PM',
  '3-5pm': '3:00 PM - 5:00 PM',
  '6-8pm': '6:00 PM - 8:00 PM',
  'Morning (9 AM - 12 PM)': 'Morning (9 AM - 12 PM)',
  'Afternoon (12 PM - 3 PM)': 'Afternoon (12 PM - 3 PM)',
  'Evening (3 PM - 6 PM)': 'Evening (3 PM - 6 PM)',
};

export const EditPickupModal = ({
  shipmentId,
  currentBoxIds,
  currentPickupDate,
  currentTimeSlot,
  isOpen,
  onClose,
  onSuccess,
}: EditPickupModalProps) => {
  const { toast } = useToast();
  const [availableBoxes, setAvailableBoxes] = useState<Box[]>([]);
  const [selectedBoxIds, setSelectedBoxIds] = useState<string[]>([]);
  const [newPickupDate, setNewPickupDate] = useState<Date>();
  const [isAddingBoxes, setIsAddingBoxes] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Check if pickup is within 24 hours
  const hoursUntilPickup = differenceInHours(currentPickupDate, new Date());
  const isLocked = hoursUntilPickup < 24;

  useEffect(() => {
    if (isOpen && !isLocked) {
      fetchAvailableBoxes();
    }
  }, [isOpen, isLocked]);

  const fetchAvailableBoxes = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch all boxes at home
      const { data: boxesData, error: boxesError } = await supabase
        .from('boxes')
        .select('id, box_number, name, room, location_status')
        .eq('user_id', user.id)
        .eq('location_status', 'home')
        .order('created_at', { ascending: false });

      if (boxesError) throw boxesError;

      // Check which boxes have scheduled pickups
      const { data: upcomingShipments } = await supabase
        .from('shipments')
        .select('box_ids')
        .eq('user_id', user.id)
        .eq('type', 'to_storage')
        .eq('status', 'scheduled')
        .gte('pickup_date', new Date().toISOString());

      const scheduledBoxIds = new Set(
        upcomingShipments?.flatMap(s => s.box_ids) || []
      );

      // Filter out boxes already in current shipment
      const available = (boxesData || [])
        .filter(box => !currentBoxIds.includes(box.id))
        .map(box => ({
          ...box,
          hasScheduledPickup: scheduledBoxIds.has(box.id)
        }));

      setAvailableBoxes(available);
    } catch (error) {
      console.error('Error fetching available boxes:', error);
      toast({
        title: "Error",
        description: "Failed to load available boxes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddBoxes = async () => {
    if (selectedBoxIds.length === 0) {
      toast({
        title: "No boxes selected",
        description: "Please select at least one box to add",
        variant: "destructive",
      });
      return;
    }

    setIsAddingBoxes(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-pickup', {
        body: {
          shipmentId,
          operation: 'add_boxes',
          additionalBoxIds: selectedBoxIds,
        },
      });

      if (error) throw error;

      toast({
        title: "Boxes added successfully",
        description: `${selectedBoxIds.length} box${selectedBoxIds.length > 1 ? 'es' : ''} added to your pickup`,
      });

      setSelectedBoxIds([]);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error adding boxes:', error);
      toast({
        title: "Failed to add boxes",
        description: error.message || "An error occurred while adding boxes",
        variant: "destructive",
      });
    } finally {
      setIsAddingBoxes(false);
    }
  };

  const handleReschedule = async () => {
    if (!newPickupDate) {
      toast({
        title: "No date selected",
        description: "Please select a new pickup date",
        variant: "destructive",
      });
      return;
    }

    setIsRescheduling(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-pickup', {
        body: {
          shipmentId,
          operation: 'reschedule',
          newPickupDate: newPickupDate.toISOString(),
        },
      });

      if (error) throw error;

      toast({
        title: "Pickup rescheduled",
        description: `Your pickup has been rescheduled to ${format(newPickupDate, 'MMMM d, yyyy')}`,
      });

      setNewPickupDate(undefined);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error rescheduling pickup:', error);
      toast({
        title: "Failed to reschedule",
        description: error.message || "An error occurred while rescheduling",
        variant: "destructive",
      });
    } finally {
      setIsRescheduling(false);
    }
  };

  const toggleBox = (boxId: string) => {
    setSelectedBoxIds(prev =>
      prev.includes(boxId)
        ? prev.filter(id => id !== boxId)
        : [...prev, boxId]
    );
  };

  const handleCancelPickup = async () => {
    setIsCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-pickup', {
        body: {
          shipmentId,
          operation: 'cancel',
        },
      });

      if (error) throw error;

      toast({
        title: "Pickup cancelled",
        description: "Your pickup has been cancelled and you'll receive a confirmation email shortly",
      });

      setShowCancelDialog(false);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error cancelling pickup:', error);
      toast({
        title: "Failed to cancel pickup",
        description: error.message || "An error occurred while cancelling",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const totalAddedCost = selectedBoxIds.length * 9.99;

  if (isLocked) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pickup Locked</DialogTitle>
            <DialogDescription>
              Your pickup is scheduled within 24 hours and cannot be modified.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-3 p-4 border border-border rounded-lg bg-muted/50">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1">
              <p className="text-sm font-medium">
                Pickup in {hoursUntilPickup} hours
              </p>
              <p className="text-xs text-muted-foreground">
                Modifications are locked 24 hours before scheduled time
              </p>
            </div>
          </div>
          <Button onClick={onClose}>Close</Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Pickup</DialogTitle>
          <DialogDescription>
            Add boxes or reschedule your pickup for {format(currentPickupDate, 'MMMM d, yyyy')}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="add-boxes" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="add-boxes">Add Boxes</TabsTrigger>
            <TabsTrigger value="reschedule">Reschedule Date</TabsTrigger>
          </TabsList>

          <TabsContent value="add-boxes" className="space-y-4">
            <div>
              <h4 className="font-semibold mb-3">Available Boxes ({selectedBoxIds.length} selected)</h4>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : availableBoxes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No additional boxes available</p>
                  <p className="text-sm">All your home boxes are already scheduled</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {availableBoxes.map((box) => (
                    <div
                      key={box.id}
                      className={cn(
                        "flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all",
                        selectedBoxIds.includes(box.id)
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50",
                        box.hasScheduledPickup && "opacity-50 cursor-not-allowed"
                      )}
                      onClick={() => !box.hasScheduledPickup && toggleBox(box.id)}
                    >
                      <Checkbox
                        checked={selectedBoxIds.includes(box.id)}
                        disabled={box.hasScheduledPickup}
                        onCheckedChange={() => toggleBox(box.id)}
                      />
                      <div className="flex-1">
                        <div className="font-medium flex items-center gap-2">
                          {box.name}
                          {box.hasScheduledPickup && (
                            <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded-full">
                              Already Scheduled
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {box.box_number} · {box.room}
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-primary">
                        $9.99/mo
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedBoxIds.length > 0 && (
              <div className="p-4 border-2 border-primary/20 rounded-lg bg-primary/5">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Additional Monthly Cost</span>
                  <span className="text-lg font-bold text-primary">
                    +${totalAddedCost.toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Adding {selectedBoxIds.length} box{selectedBoxIds.length > 1 ? 'es' : ''} × $9.99/month
                </p>
              </div>
            )}

            <Button
              onClick={handleAddBoxes}
              disabled={isAddingBoxes || selectedBoxIds.length === 0}
              className="w-full"
            >
              {isAddingBoxes ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                `Add Selected Boxes (${selectedBoxIds.length})`
              )}
            </Button>
          </TabsContent>

          <TabsContent value="reschedule" className="space-y-4">
            <div>
              <h4 className="font-semibold mb-3">Select New Date</h4>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !newPickupDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newPickupDate ? format(newPickupDate, "PPP") : "Select new pickup date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={newPickupDate}
                    onSelect={setNewPickupDate}
                    disabled={(date) => {
                      const minDate = addDays(new Date(), 1);
                      minDate.setHours(0, 0, 0, 0);
                      
                      // Disable if date is in past, is Sunday, or is within 24 hours
                      const isSunday = date.getDay() === 0;
                      const isWithin24Hours = differenceInHours(date, new Date()) < 24;
                      
                      return date < minDate || isSunday || isWithin24Hours;
                    }}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="p-4 border border-border rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Time Slot (Unchanged)</p>
                  <p className="text-sm text-muted-foreground">
                    {timeSlotDisplay[currentTimeSlot] || currentTimeSlot}
                  </p>
                </div>
              </div>
            </div>

            {newPickupDate && (
              <div className="p-4 border-2 border-primary/20 rounded-lg bg-primary/5">
                <p className="text-sm font-medium mb-1">New Pickup Date</p>
                <p className="text-lg font-semibold text-primary">
                  {format(newPickupDate, 'EEEE, MMMM d, yyyy')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Your pickup time slot will remain the same
                </p>
              </div>
            )}

            <Button
              onClick={handleReschedule}
              disabled={isRescheduling || !newPickupDate}
              className="w-full"
            >
              {isRescheduling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Rescheduling...
                </>
              ) : (
                "Update Date"
              )}
            </Button>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6 pt-6 border-t border-border">
          <Button
            variant="destructive"
            onClick={() => setShowCancelDialog(true)}
            className="w-full sm:w-auto"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Cancel Entire Pickup
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Pickup?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel your entire pickup scheduled for {format(currentPickupDate, 'MMMM d, yyyy')}. 
              All {currentBoxIds.length} box{currentBoxIds.length > 1 ? 'es' : ''} will be removed from the pickup, 
              and your Stripe subscription will be updated accordingly.
              <br /><br />
              You'll receive a confirmation email once the cancellation is complete.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling}>Keep Pickup</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleCancelPickup();
              }}
              disabled={isCancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isCancelling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                "Yes, Cancel Pickup"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
